import React from 'react';

interface CalendarEvent {
  id: string;
  title: string;
  date: string;
  time: string;
  type: 'preparation' | 'celebration' | 'community' | 'global';
}

interface WeekViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  onEventClick: (eventId: string) => void;
}

const WeekView: React.FC<WeekViewProps> = ({ currentDate, events, onEventClick }) => {
  const getWeekDays = () => {
    const days: Date[] = [];
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }

    return days;
  };

  const getEventsForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(event => event.date === dateStr);
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'preparation':
        return 'bg-peaceful-teal text-white';
      case 'celebration':
        return 'bg-accent text-accent-foreground';
      case 'community':
        return 'bg-secondary text-secondary-foreground';
      case 'global':
        return 'bg-primary text-primary-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const weekDays = getWeekDays();
  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="bg-card rounded-lg shadow-sm overflow-hidden">
      {/* Week day headers */}
      <div className="grid grid-cols-8 border-b border-border">
        <div className="p-4 bg-muted"></div>
        {weekDays.map((day) => (
          <div
            key={day.toISOString()}
            className={`p-4 text-center ${
              isToday(day) ? 'bg-primary/10' : 'bg-muted'
            }`}
          >
            <div className="font-body text-xs text-muted-foreground">
              {day.toLocaleDateString('en-US', { weekday: 'short' })}
            </div>
            <div
              className={`mt-1 w-8 h-8 mx-auto flex items-center justify-center rounded-full ${
                isToday(day)
                  ? 'bg-primary text-primary-foreground font-bold'
                  : 'text-foreground'
              }`}
            >
              {day.getDate()}
            </div>
          </div>
        ))}
      </div>

      {/* Time grid */}
      <div className="overflow-y-auto max-h-[600px]">
        {hours.map((hour) => (
          <div key={hour} className="grid grid-cols-8 border-b border-border min-h-20">
            <div className="p-2 bg-muted text-xs text-muted-foreground text-right">
              {hour.toString().padStart(2, '0')}:00
            </div>
            {weekDays.map((day) => {
              const dayEvents = getEventsForDate(day).filter(event => {
                const eventHour = parseInt(event.time.split(':')[0]);
                return eventHour === hour;
              });

              return (
                <div
                  key={`${day.toISOString()}-${hour}`}
                  className="p-2 border-r border-border hover:bg-muted/50 transition-colors duration-200"
                >
                  {dayEvents.map((event) => (
                    <button
                      key={event.id}
                      onClick={() => onEventClick(event.id)}
                      className={`w-full text-left px-2 py-1 rounded text-xs font-body mb-1 ${getEventTypeColor(
                        event.type
                      )} hover:opacity-80 transition-opacity duration-200`}
                    >
                      <div className="font-bold truncate">{event.title}</div>
                      <div className="opacity-80">{event.time}</div>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default WeekView;